import React, { useState, useEffect, useMemo, useRef, useCallback, useId } from 'react';
import {
  Box,
  Button,
  FormControlLabel,
  Checkbox,
  Typography,
  CircularProgress,
  TextField,
  InputAdornment,
  SxProps,
  Theme
} from '@mui/material';
import './CompanyProfile.scss';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchCompanyProfileStart,
  updateCompanyProfileStart,
  setCompanySubmitt,
  setShowSuccessPopUp,
} from '../../../redux/slices/sellerProfileSlice';
import { getAllRegistrationData } from '../../../redux/slices/sellerRegSlice';
import GetLabels from '../GetLabels';
import { iso2To3 } from '../../../constants/apiUtil';
import SuperModalRegistration, { AppliedPOSItem } from '../../SellerRegistration/SuperModalRegistration';
import { validate, FieldError, CommonSelect, CommonMultiSelect, CommonDatePicker, SvgIcon, rankDropdownCode } from '../../SellerRegistration/SellerRegistrationForm';
import {
  getSelectFloatingLabelSx,
  getSelectTriggerSx,
  getSelectArrowSx,
  selectDropdownSx,
  getSelectOptionSx,
  dropdownCheckmarkSx,
  renderValueSelectedStyle,
  fallbackTextSx,
} from '../../SellerRegistration/SellerRegistrationForm.styles';

// Legacy business-model values → current codes (matched by code or legacy display
// name): Online Travel Agency (OTA) → Travel Agency (AG); Corporate Booking Tool
// (CB) → Online Booking Tool (OB).
const BIZ_MODEL_LEGACY_MAP: Record<string, string> = {
  OTA: 'AG',
  'Online Travel Agency': 'AG',
  CB: 'OB',
  'Corporate Booking Tool': 'OB',
};

// ─── CPMultiSelect — like CommonMultiSelect but shows first 2 values + +N ─────
const CPMultiSelect = ({
  values,
  onChange,
  menuList,
  placeholder,
  label,
  error,
  errorMessage,
  required,
  triggerRef,
}: {
  values: string[];
  onChange: (values: string[]) => void;
  menuList: string[];
  placeholder?: string;
  label?: string;
  error?: boolean;
  errorMessage?: string;
  required?: boolean;
  triggerRef?: React.Ref<HTMLDivElement>;
}) => {
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const errorId = useId();
  const labelId = useId();
  const listboxId = useId();
  const optionIdBase = useId();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const listboxRef = useRef<HTMLDivElement>(null);
  const isActive = open || focused || values.length > 0;

  const openDropdown = useCallback(() => {
    const firstSelected = values.length > 0 ? menuList.indexOf(values[0]) : -1;
    setActiveIndex(firstSelected >= 0 ? firstSelected : -1);
    setOpen(true);
  }, [values, menuList]);

  const toggleOption = useCallback(
    (opt: string) => {
      if (values.includes(opt)) {
        onChange(values.filter((v) => v !== opt));
      } else {
        onChange([...values, opt]);
      }
    },
    [values, onChange]
  );

  useEffect(() => {
    if (!open) return;
    const handleOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  useEffect(() => {
    if (!open || activeIndex < 0 || !listboxRef.current) return;
    const options = listboxRef.current.querySelectorAll<HTMLElement>('[role="option"]');
    options[activeIndex]?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, open]);

  const displayValue = values.length > 2
    ? `${values.slice(0, 2).join(', ')} +${values.length - 2}`
    : values.length > 0
      ? values.join(', ')
      : '';

  return (
    <Box ref={wrapperRef} sx={{ width: '100%', position: 'relative', mt: { xs: 1 } }}>
      {label && (
        <Typography id={labelId} sx={getSelectFloatingLabelSx(isActive)}>
          {label}
          <span style={{ color: '#FF333A' }}>{required ? '*' : null}</span>
        </Typography>
      )}
      <Box
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        aria-labelledby={label ? labelId : undefined}
        aria-activedescendant={open && activeIndex >= 0 ? `${optionIdBase}-${activeIndex}` : undefined}
        aria-invalid={error ? true : undefined}
        aria-required={required || undefined}
        aria-describedby={error && errorMessage ? errorId : undefined}
        tabIndex={0}
        ref={triggerRef}
        onClick={() => { open ? setOpen(false) : openDropdown(); }}
        onFocus={() => setFocused(true)}
        onBlur={(e: React.FocusEvent) => {
          setFocused(false);
          if (!wrapperRef.current?.contains(e.relatedTarget as Node)) setOpen(false);
        }}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (!open) {
            if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') { e.preventDefault(); openDropdown(); }
          } else {
            if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex((i) => Math.min(i + 1, menuList.length - 1)); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex((i) => Math.max(i - 1, 0)); }
            else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (activeIndex >= 0 && activeIndex < menuList.length) toggleOption(menuList[activeIndex]); }
            else if (e.key === 'Home') { e.preventDefault(); setActiveIndex(0); }
            else if (e.key === 'End') { e.preventDefault(); setActiveIndex(menuList.length - 1); }
            else if (e.key === 'Escape' || e.key === 'Tab') setOpen(false);
          }
        }}
        sx={getSelectTriggerSx(!!error)}
      >
        {displayValue ? (
          <span style={renderValueSelectedStyle}>{displayValue}</span>
        ) : !label && placeholder ? (
          <Typography sx={fallbackTextSx}>{placeholder}</Typography>
        ) : (
          <span />
        )}
        <Box sx={getSelectArrowSx(open)} />
      </Box>
      {open && (
        <Box
          ref={listboxRef}
          role="listbox"
          id={listboxId}
          aria-multiselectable="true"
          aria-labelledby={label ? labelId : undefined}
          onMouseDown={(e) => e.preventDefault()}
          sx={selectDropdownSx}
        >
          {menuList.map((opt, i) => (
            <Box
              key={opt}
              id={`${optionIdBase}-${i}`}
              role="option"
              aria-selected={values.includes(opt)}
              onClick={() => toggleOption(opt)}
              sx={getSelectOptionSx(values.includes(opt), i === activeIndex)}
            >
              {opt}
              {values.includes(opt) && (
                <SvgIcon src="/images/registration/checkmarkDropdown.svg" size={20} sx={dropdownCheckmarkSx} />
              )}
            </Box>
          ))}
        </Box>
      )}
      {error && errorMessage && <FieldError id={errorId} message={errorMessage} />}
    </Box>
  );
};

export const fieldIconSx: SxProps<Theme> = {
  width: '20px',
  height: '20px',
  objectFit: 'contain',
  marginBottom: '20px'
};

// ─── CPTextField — local replica of CommonTextField with customisable styles ──
const CPTextField = ({
  label,
  helperText,
  startIcon,
  endAdornment,
  inputStyle,
  value,
  onChange,
  onFocus,
  onBlur,
  error,
  errorMessage,
  ariaLabel,
  autoComplete,
  required,
  disabled,
  inputRef
}: {
  label: string;
  helperText?: string;
  startIcon?: React.ReactNode;
  endAdornment?: React.ReactNode;
  inputStyle?: React.CSSProperties;
  value?: string;
  onChange?: (val: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  error?: boolean;
  required?: boolean;
  errorMessage?: string;
  ariaLabel?: string;
  autoComplete?: string;
  disabled?: boolean;
  inputRef?: React.Ref<HTMLInputElement>;
}) => {
  const [focused, setFocused] = useState(false);
  const errorId = useId();
  const hasValue = value !== undefined ? value.length > 0 : false;

  return (
    <Box>
      <TextField
        required={required}
        label={label}
        variant="standard"
        inputRef={inputRef}
        fullWidth
        disabled={disabled}
        value={value ?? ''}
        onChange={(e) => onChange?.(e.target.value)}
        InputLabelProps={{ shrink: focused || hasValue }}
        InputProps={{
          onFocus: () => { setFocused(true); onFocus?.(); },
          onBlur: () => { setFocused(false); onBlur?.(); },
          ...(startIcon && {
            startAdornment: <InputAdornment position="start">{startIcon}</InputAdornment>
          }),
          ...(endAdornment && {
            endAdornment: <InputAdornment position="start" disablePointerEvents>{endAdornment}</InputAdornment>
          })
        }}
        inputProps={{
          'aria-label': ariaLabel || undefined,
          'aria-invalid': error ? true : undefined,
          'aria-describedby': error && errorMessage ? errorId : undefined,
          ...(autoComplete && { autoComplete }),
          ...(inputStyle && { style: inputStyle })
        }}
        helperText={!error ? helperText : null}
        sx={{
          // ── Label ────────────────────────────────────────────────────────
          '& label': {
            fontFamily: 'AC Nord Text',
            fontWeight: 500,
            fontSize: { xs: '14px', md: '16px' },
            lineHeight: '20px',
            letterSpacing: '0px',
            color: 'var(--Sem-text-colour-text-primary, #000000)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            '&.Mui-focused': { color: 'var(--Sem-text-colour-text-secondary, #6D6D6F)', fontWeight: 500 },
            '&.MuiInputLabel-shrink': { color: 'var(--Sem-text-colour-text-secondary, #6D6D6F)', fontWeight: 500 },
            '& .MuiFormLabel-asterisk': { color: '#FF333A' },
          },
          // ── Input text ───────────────────────────────────────────────────
          '& input': {
            fontFamily: 'AC Nord Text',
            fontWeight: 400,
            fontSize: { xs: '14px', md: '16px' },
            lineHeight: '20px',
            letterSpacing: '0px',
            color: 'var(--Sem-text-colour-text-primary, #000000)',
          },
          // ── Underline ────────────────────────────────────────────────────
          '& .MuiInput-underline:before': { borderBottomColor: 'var(--Sem-brdr-colour-border-secondary, #C9CACC)' },
          '& .MuiInput-underline:after': { borderBottomColor: 'var(--Sem-brdr-colour-border-secondary, #C9CACC)' },
          '& .MuiInput-underline.Mui-focused:after': { borderBottomColor: 'var(--Sem-brdr-colour-border-secondary, #C9CACC)' },
          '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: 'var(--Sem-brdr-colour-border-secondary, #C9CACC)' },
          // ── Error underline ──────────────────────────────────────────────
          ...(error && {
            '& .MuiInput-underline:before': { borderBottomColor: 'var(--Sem-text-colour-text-error, #851109)' },
            '& .MuiInput-underline:after': { borderBottomColor: 'var(--Sem-text-colour-text-error, #851109)' },
          }),
          // ── Label offset when a start icon is present ────────────────────
          ...(startIcon && {
            '& label:not(.MuiInputLabel-shrink)': {
              transform: 'translate(34px, 20px) scale(1)',
              maxWidth: 'calc(100% - 50px)',
            },
             // Keep the shrunk (floating) label to the right of the icon too
            '& label.MuiInputLabel-shrink': {
              marginLeft: '29px',
            },
          }),
        }}
      />
      {error && errorMessage && <FieldError id={errorId} message={errorMessage} />}
    </Box>
  );
};

// ─── helpers ──────────────────────────────────────────────────────────────────
const asStr = (v: any): string => {
  if (!v) return '';
  if (Array.isArray(v)) return String(v[0] ?? '');
  return String(v);
};
const asArr = (v: any): string[] => {
  if (!v) return [];
  if (Array.isArray(v)) return v.map(String);
  return String(v)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
};

function CompanyProfile() {
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const dispatch = useDispatch();
  const lang = i18n.language === 'fr' ? 'fr' : 'en';

  const {
    compProfile,
    defaultCompData,
    seller_id,
    updating,
    compSubmitting,
    error,
    spPageText,
    company_name,
  } = useSelector((state: any) => state.sellerProfile);

  const {
    pos: regPos,
    supportedFlow: regSupportedFlow,
    businessModel: regBusinessModel,
    customerTypes: regCustomerTypes,
    apiSchemas: regApiSchemas,
  } = useSelector((state: any) => state.sellerReg);

  // ── local form state ────────────────────────────────────────────────────────
  const [form, setForm] = useState<any>({
    api_schema_version: [],
    company_address: '',
    company_website: '',
    customer_type: '',
    business_model_type: [],
    implementation_start_date: null,
    support_flow: '',
    business_contact_first_name: '',
    business_contact_last_name: '',
    business_phone_no: '',
    business_email: '',
    admin_contact_first_name: '',
    admin_contact_last_name: '',
    admin_contact_phone: '',
    admin_contact_email: '',
    iata_number: '',
  });
  const [adminSameAsBusiness, setAdminSameAsBusiness] = useState(false);
  const [ready, setReady] = useState(false);
  const [posModalOpen, setPosModalOpen] = useState(false);
  const [localPosItems, setLocalPosItems] = useState<AppliedPOSItem[] | null>(null);
  const [localIataValues, setLocalIataValues] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isDirty, setIsDirty] = useState(false);
  const editBtnRef = useRef<HTMLButtonElement>(null);

  // ── refs used to focus the first field with a validation error on submit ────
  const companyAddressRef = useRef<HTMLInputElement>(null);
  const companyWebsiteRef = useRef<HTMLInputElement>(null);
  const customerTypeRef = useRef<HTMLDivElement>(null);
  const businessModelRef = useRef<HTMLDivElement>(null);
  const supportFlowRef = useRef<HTMLDivElement>(null);
  const apiSchemaRef = useRef<HTMLDivElement>(null);
  const businessFirstNameRef = useRef<HTMLInputElement>(null);
  const businessLastNameRef = useRef<HTMLInputElement>(null);
  const businessPhoneRef = useRef<HTMLInputElement>(null);
  const businessEmailRef = useRef<HTMLInputElement>(null);
  const adminFirstNameRef = useRef<HTMLInputElement>(null);
  const adminLastNameRef = useRef<HTMLInputElement>(null);
  const adminPhoneRef = useRef<HTMLInputElement>(null);
  const adminEmailRef = useRef<HTMLInputElement>(null);

  // ── Date picker: grow the form card so the calendar isn't clipped ───────────
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerHeight, setPickerHeight] = useState(0);
  const cardRef = useRef<HTMLDivElement>(null);

  const lbl = (key: string) => GetLabels(`spPageText.${key}`, spPageText);

  // ── fetch needed data ───────────────────────────────────────────────────────
  useEffect(() => {
    dispatch(getAllRegistrationData());
    if (seller_id && (!compProfile || Object.keys(compProfile).length === 0)) {
      dispatch(fetchCompanyProfileStart({ seller_id, language: i18n.language }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── populate form when profile loads ───────────────────────────────────────
  useEffect(() => {
    if (!compProfile || Object.keys(compProfile).length === 0) return;

    // Parse implementation_start_date into a Date object for CommonDatePicker
    // Use local-date construction to avoid UTC-to-local day shift (e.g. "2026-08-15"
    // parsed as UTC midnight appears as Aug 14 in UTC-5).
    let implDate: Date | null = null;
    try {
      const raw = asStr(compProfile.implementation_start_date);
      if (raw) {
        const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (isoMatch) {
          // ISO / date-only string — construct as local midnight to avoid UTC shift
          implDate = new Date(Number(isoMatch[1]), Number(isoMatch[2]) - 1, Number(isoMatch[3]));
        } else {
          // Non-ISO format (timestamp, locale string, etc.) — let Date parse it
          const d = new Date(raw);
          if (!isNaN(d.getTime())) implDate = d;
        }
      }
    } catch (_) {}

    setForm({
      api_schema_version: asArr(compProfile.api_schema_version),
      company_address: asStr(compProfile.company_address),
      company_website: asStr(compProfile.company_website),
      customer_type: asStr(compProfile.customer_type),
      business_model_type: asArr(compProfile.business_model_type),
      implementation_start_date: implDate,
      support_flow: asStr(compProfile.support_flow),
      business_contact_first_name: asStr(compProfile.business_contact_first_name),
      business_contact_last_name: asStr(compProfile.business_contact_last_name),
      business_phone_no: asStr(compProfile.business_phone_no),
      business_email: asStr(compProfile.business_email),
      admin_contact_first_name: asStr(compProfile.admin_contact_first_name),
      admin_contact_last_name: asStr(compProfile.admin_contact_last_name),
      admin_contact_phone: asStr(compProfile.admin_contact_phone),
      admin_contact_email: asStr(compProfile.admin_contact_email),
      iata_number: asStr(compProfile.iata_number),
    });
    // Initialise local POS state from compProfile
    const iataMap: Record<string, string> = compProfile?.pos_iata_map || {};
    const initialPosItems: AppliedPOSItem[] = (compProfile?.pos_type || []).map((posEntry: string) => {
      const parts = posEntry.trim().split(' ');
      const code = parts[parts.length - 1];
      const name = parts.length > 1 ? parts.slice(0, -1).join(' ') : posEntry;
      const iso3 = iso2To3[code] || '';
      return { code, name, flag: iso3 ? `/images/flags/${iso3}.png` : '', iata: iataMap[code] || '' };
    });
    setLocalPosItems(initialPosItems);
    setLocalIataValues(iataMap);

    setReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compProfile]);

  // ── error toast ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (error) {
      dispatch(
        setShowSuccessPopUp({
          message: t('sellerProfile.dashboard.errorGeneral'),
          successType: 'error',
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  // ── success redirect ────────────────────────────────────────────────────────
  useEffect(() => {
    if (compSubmitting) {
      dispatch(setShowSuccessPopUp(t('sellerProfile.dashboard.editCompanyProfileSuccess') as string));
      dispatch(setCompanySubmitt(false));
      navigate(`/${i18n.language}/seller-profile`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compSubmitting]);

  // ── dropdown options ────────────────────────────────────────────────────────
  const apiSchemaOptions: string[] = useMemo(() => {
    let options: string[] = [];
    const fromDefault = (defaultCompData?.api_schema || []).map(
      (s: any) => s.api_schema_version
    );
    if (fromDefault.length) {
      options = fromDefault;
    } else {
      const fromReg = (regApiSchemas?.[lang] || []).map((s: any) => s.desc);
      if (fromReg.length) {
        options = fromReg;
      } else {
        options = asArr(compProfile?.api_schema_version);
      }
    }
    // Build a desc→code map inline to identify "Other" by code AS001
    const descToCode: Record<string, string> = {};
    (regApiSchemas?.[lang] || []).forEach((s: any) => { if (s.desc && s.code) descToCode[s.desc] = s.code; });
    (defaultCompData?.api_schema || []).forEach((s: any) => { if (s.api_schema_version && s.code) descToCode[s.api_schema_version] = s.code; });
    // Sort options with "Other" (AS001) at the end
    return [...options].sort((a, b) => {
      const aIsOther = descToCode[a] === 'AS001';
      const bIsOther = descToCode[b] === 'AS001';
      if (aIsOther && !bIsOther) return 1;
      if (!aIsOther && bIsOther) return -1;
      return (a || '').localeCompare(b || '');
    });
  }, [defaultCompData, regApiSchemas, lang, compProfile]);

  // Map api_schema codes → display labels
  const apiSchemaCodeToDesc = useMemo(() => {
    const map: Record<string, string> = {};
    (regApiSchemas?.[lang] || []).forEach((s: any) => {
      if (s.code && s.desc) map[s.code] = s.desc;
    });
    (defaultCompData?.api_schema || []).forEach((s: any) => {
      if (s.code && s.api_schema_version) map[s.code] = s.api_schema_version;
    });
    return map;
  }, [regApiSchemas, defaultCompData, lang]);

  const customerTypeOptions: { id: string; label: string }[] = useMemo(() => {
    const fromDefault = (defaultCompData?.customer_types || []).map((ct: any) => ({
      id: ct.customer_type_id,
      label: lang === 'fr' ? (ct.customer_type_fr || ct.customer_type) : ct.customer_type,
    }));
    const raw = fromDefault.length
      ? fromDefault
      : (regCustomerTypes?.[lang] || []).map((ct: any) => ({ id: ct.code, label: ct.desc }));
    // Shared ranking (Corporate CO, Leisure CT001, Both CT002); label as tiebreaker.
    return [...raw].sort((a, b) => {
      const diff = rankDropdownCode(a.id) - rankDropdownCode(b.id);
      return diff !== 0 ? diff : a.label.localeCompare(b.label);
    });
  }, [defaultCompData, regCustomerTypes, lang]);

  const bizModelOptions: { id: string; label: string }[] = useMemo(() => {
    const fromDefault = (defaultCompData?.business_model || []).map((bm: any) => ({
      id: bm.business_model_id,
      label: lang === 'fr'
        ? (bm.business_model_type_fr || bm.business_model_type)
        : bm.business_model_type,
    }));
    // Shared ranking so "Other" (BM002) sorts last; label as tiebreaker.
    const byRank = (a: any, b: any) => {
      const diff = rankDropdownCode(a.id) - rankDropdownCode(b.id);
      return diff !== 0 ? diff : a.label.localeCompare(b.label);
    };
    if (fromDefault.length) return fromDefault.sort(byRank);
    // fall back to sellerReg data
    return (regBusinessModel?.[lang] || [])
      .map((bm: any) => ({ id: bm.code, label: bm.desc }))
      .sort(byRank);
  }, [defaultCompData, regBusinessModel, lang]);

  const supportedFlowOptions: { code: string; desc: string }[] = useMemo(() => {
    return [...(regSupportedFlow?.[lang] || [])].sort((a, b) => {
      const aIsAll = (a.code || '').toUpperCase() === 'SF003';
      const bIsAll = (b.code || '').toUpperCase() === 'SF003';
      if (aIsAll) return -1;
      if (bIsAll) return 1;
      return (a.desc || '').localeCompare(b.desc || '');
    });
  }, [regSupportedFlow, lang]);

  const supportedFlowCodeToDesc = useMemo(() => {
    const map: Record<string, string> = {};
    supportedFlowOptions.forEach((sf) => { map[sf.code] = sf.desc; });
    return map;
  }, [supportedFlowOptions]);

  const supportedFlowDescToCode = useMemo(() => {
    const map: Record<string, string> = {};
    supportedFlowOptions.forEach((sf) => { map[sf.desc] = sf.code; });
    return map;
  }, [supportedFlowOptions]);

  // ── Normalize customer_type to label when options load ──────────────────────
  useEffect(() => {
    if (!form.customer_type || customerTypeOptions.length === 0) return;
    if (customerTypeOptions.some((opt) => opt.label === form.customer_type)) return;
    const match = customerTypeOptions.find(
      (opt) => opt.id === form.customer_type
    );
    if (match) setForm((prev: any) => ({ ...prev, customer_type: match.label }));
  }, [customerTypeOptions, form.customer_type]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Detect a stored customer_type that no longer matches any current option ──
  // (options changed on the API side). We keep showing the legacy value but flag
  // it so the user must re-select before saving.
  const customerTypeIsLegacy = useMemo(() => {
    if (!form.customer_type || customerTypeOptions.length === 0) return false;
    return !customerTypeOptions.some(
      (opt) => opt.label === form.customer_type || opt.id === form.customer_type
    );
  }, [customerTypeOptions, form.customer_type]);

  // ── Normalize business_model_type to labels when options load ───────────────
  // Also migrates legacy values (Online Travel Agency → Travel Agency, Corporate
  // Booking Tool → Online Booking Tool) and de-duplicates the result.
  useEffect(() => {
    if (!form.business_model_type?.length || bizModelOptions.length === 0) return;
    const idToLabel = new Map(
      bizModelOptions.map((o) => [o.id, o.label] as [string, string])
    );
    const labelSet = new Set(bizModelOptions.map((o) => o.label));
    const normalized = form.business_model_type.map((v: string) => {
      const canonical = BIZ_MODEL_LEGACY_MAP[v] || v; // legacy code/name → current code
      return idToLabel.get(canonical) ?? (labelSet.has(canonical) ? canonical : v);
    });
    const deduped = Array.from(new Set(normalized));
    const unchanged =
      deduped.length === form.business_model_type.length &&
      deduped.every((v, i) => v === form.business_model_type[i]);
    if (unchanged) return;
    setForm((prev: any) => ({ ...prev, business_model_type: deduped }));
  }, [bizModelOptions, form.business_model_type]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Normalize api_schema_version to labels when options load ────────────────
  useEffect(() => {
    if (!form.api_schema_version?.length || apiSchemaOptions.length === 0) return;
    const needsNormalization = form.api_schema_version.some(
      (v: string) => !apiSchemaOptions.includes(v)
    );
    if (!needsNormalization) return;
    const otherOption = apiSchemaOptions.find((opt: string) => apiSchemaCodeToDesc['AS001'] === opt);
    const normalized = (form.api_schema_version as string[]).map((v: string) => {
      if (apiSchemaOptions.includes(v)) return v;
      const desc = apiSchemaCodeToDesc[v];
      if (desc && apiSchemaOptions.includes(desc)) return desc;
      // Value not in options — treat it as "Other" if that option exists
      return otherOption ?? v;
    });
    const deduped = Array.from(new Set(normalized)) as string[];
    setForm((prev: any) => ({ ...prev, api_schema_version: deduped }));
  }, [apiSchemaOptions, apiSchemaCodeToDesc, form.api_schema_version]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── IATA logic ──────────────────────────────────────────────────────────────
  // Language-safe Travel Agency detection from sellerReg data (always loaded via
  // getAllRegistrationData) — a reliable fallback when defaultCompData (which
  // carries the iata_mandatory flag) isn't populated on this page.
  const travelAgencyCode = useMemo(
    () =>
      (regBusinessModel?.en ?? []).find(
        (i: any) => i.desc?.trim().toLowerCase() === 'travel agency'
      )?.code ?? '',
    [regBusinessModel]
  );

  const bizModelLabelToCode = useMemo(() => {
    const map: Record<string, string> = {};
    (regBusinessModel?.[lang] ?? []).forEach((i: any) => {
      if (i.desc && i.code) map[i.desc] = i.code;
    });
    return map;
  }, [regBusinessModel, lang]);

  // True when any selected business model mandates an IATA (e.g. Travel Agency).
  // Primary signal is defaultCompData's iata_mandatory flag; falls back to a
  // language-safe Travel Agency match via sellerReg data. Matches by label (en/fr),
  // id, or code so it stays correct before/after id→label normalization.
  const modelsRequireIata = useCallback(
    (models: string[]): boolean => {
      const list = defaultCompData?.business_model || [];
      const byMandatory = list.some((bm: any) => {
        const isSelected =
          models.includes(bm.business_model_type) ||
          models.includes(bm.business_model_type_fr) ||
          models.includes(bm.business_model_id);
        return isSelected && Number(bm.iata_mandatory) === 1;
      });
      if (byMandatory) return true;
      if (!travelAgencyCode) return false;
      return models.some(
        (m) => m === travelAgencyCode || bizModelLabelToCode[m] === travelAgencyCode
      );
    },
    [defaultCompData?.business_model, travelAgencyCode, bizModelLabelToCode]
  );

  const anyIataRequired = useMemo(
    () => modelsRequireIata(form.business_model_type),
    [modelsRequireIata, form.business_model_type]
  );

  // Keep IATA tied to the business model: when no selected model requires an IATA
  // (Travel Agency never chosen, or removed while editing), drop any captured IATA.
  // Guarded on reference data being loaded so we never clear before we can tell
  // whether IATA is actually required.
  useEffect(() => {
    if (!ready) return;
    const refLoaded =
      (defaultCompData?.business_model || []).length > 0 ||
      (regBusinessModel?.en || []).length > 0;
    if (!refLoaded) return;
    if (anyIataRequired) return;
    const hasIata =
      !!form.iata_number ||
      Object.keys(localIataValues).length > 0 ||
      !!localPosItems?.some((p) => p.iata);
    if (!hasIata) return;
    setForm((prev: any) => ({ ...prev, iata_number: '' }));
    setLocalIataValues({});
    setLocalPosItems((prev) => (prev ? prev.map((p) => ({ ...p, iata: '' })) : prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, anyIataRequired, defaultCompData?.business_model, regBusinessModel]);

  // ── POS display ─────────────────────────────────────────────────────────────
  const posCodeToInfo = useMemo(() => {
    const map: Record<string, { name: string; iso3: string }> = {};
    (defaultCompData?.pos_type || []).forEach((pos: any) => {
      const name = lang === 'fr' ? (pos.pays || pos.country) : (pos.country || pos.pays);
      map[pos.pos_code] = { name, iso3: iso2To3[pos.pos_code] || '' };
    });
    (regPos?.[lang] || []).forEach((entry: any) => {
      if (entry?.code && entry?.desc) {
        map[entry.code] = { name: entry.desc, iso3: iso2To3[entry.code] || '' };
      }
    });
    return map;
  }, [defaultCompData, regPos, lang]);

  const posItems = useMemo(() => {
    return (compProfile?.pos_type || []).map((posEntry: string) => {
      const parts = posEntry.trim().split(' ');
      const code = parts[parts.length - 1];
      const info = posCodeToInfo[code];
      const name = info?.name || (parts.length > 1 ? parts.slice(0, -1).join(' ') : posEntry);
      const iso3 = info?.iso3 || '';
      const iata = compProfile?.pos_iata_map?.[code] || '';
      return { code, name, iso3, iata };
    });
  }, [compProfile, posCodeToInfo]);

  // ── Resolved POS items for display (merges localPosItems with posCodeToInfo) ─
  const displayPosItems = useMemo(() => {
    const items = localPosItems !== null ? localPosItems : posItems;
    const modalCodeSet = new Set(Object.keys(posCodeToInfo));
    return items
      .filter((p: any) => modalCodeSet.size === 0 || modalCodeSet.has(p.code))
      .map((p: any) => {
      const info = posCodeToInfo[p.code];
      return {
        ...p,
        name: info?.name || p.name,
        flag: info?.iso3 ? `/images/flags/${info.iso3}.png` : (p.flag || ''),
      };
    }).sort((a, b) => {
      const aIsOther = (a.code || '').toUpperCase() === 'OT';
      const bIsOther = (b.code || '').toUpperCase() === 'OT';
      if (aIsOther && !bIsOther) return 1;
      if (!aIsOther && bIsOther) return -1;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [localPosItems, posItems, posCodeToInfo]);

  // ── POS modal handlers ──────────────────────────────────────────────────────
  const isPosOtherItem = (item: AppliedPOSItem) =>
    (item.code || '').toUpperCase() === 'OT';

  // Called by the modal when the user selects only the "Other" (OT) entry and
  // clicks Next. The modal closes itself after this; we just show the error.
  const handlePosOtherSelected = () => {
    setLocalPosItems([]);
    setLocalIataValues({});
    setErrors((prev) => ({
      ...prev,
      pos: t('registrationStepsData.form.step1.question3.onlyOtherError'),
    }));
    setIsDirty(true);
  };

  const handlePosNext = (items: AppliedPOSItem[]) => {
    // If every selected POS is "Other", reject the selection, clear items, and show an error.
    if (items.length > 0 && items.every(isPosOtherItem)) {
      setLocalPosItems([]);
      setLocalIataValues({});
      setErrors((prev) => ({
        ...prev,
        pos: t('registrationStepsData.form.step1.question3.onlyOtherError'),
      }));
      setPosModalOpen(false);
      setIsDirty(true);
      return;
    }
    setLocalPosItems(items);
    const newIataValues: Record<string, string> = {};
    items.forEach((item) => { if (item.iata) newIataValues[item.code] = item.iata; });
    setLocalIataValues(newIataValues);
    if (errors.pos) setErrors((prev) => ({ ...prev, pos: '' }));
    setPosModalOpen(false);
    setIsDirty(true);
  };

  // ── Date picker open/measure — grows the card to fit the calendar ───────────
  const handlePickerOpenChange = useCallback((isOpen: boolean) => {
    if (!isOpen) {
      setPickerOpen(false);
      setPickerHeight(0);
      return;
    }
    setPickerOpen(true);
  }, []);

  useEffect(() => {
    if (!pickerOpen) return;
    const popup = cardRef.current?.querySelector('.day-picker') as HTMLElement | null;
    if (!popup || !cardRef.current) return;
    const cardRect = cardRef.current.getBoundingClientRect();
    const popupBottom = popup.getBoundingClientRect().bottom;
    const overflow = popupBottom - cardRect.bottom;
    setPickerHeight(overflow > 0 ? Math.ceil(cardRect.height + overflow + 16) : 0);
  }, [pickerOpen]);

  // ── field change handler ────────────────────────────────────────────────────
  const handleChange = (field: string, value: any) => {
    // Removing the last IATA-mandatory business model (e.g. Travel Agency) clears any
    // captured IATA right away, so the POS chips update without reopening the POS modal.
    const clearIata = field === 'business_model_type' && !modelsRequireIata(value);

    setForm((prev: any) => {
      const updated = { ...prev, [field]: value };
      if (clearIata) updated.iata_number = '';
      // Sync admin contact fields whenever a business contact field changes while
      // "same as business" is checked
      if (adminSameAsBusiness) {
        const syncMap: Record<string, string> = {
          business_contact_first_name: 'admin_contact_first_name',
          business_contact_last_name: 'admin_contact_last_name',
          business_phone_no: 'admin_contact_phone',
          business_email: 'admin_contact_email',
        };
        if (syncMap[field]) updated[syncMap[field]] = value;
      }
      return updated;
    });
    if (clearIata) {
      setLocalIataValues({});
      setLocalPosItems((prev) => (prev ? prev.map((p) => ({ ...p, iata: '' })) : prev));
    }
    setIsDirty(true);

    const tv = (key: string) => (key ? t(key) : '');

    // Phone fields: live validation on every keystroke, matching reg.tsx behaviour.
    // Always set or clear the error rather than waiting for a prior error to exist.
    if (field === 'business_phone_no') {
      setErrors((prev) => ({ ...prev, business_phone_no: tv(validate('phone', value)) }));
      return;
    }
    if (field === 'admin_contact_phone') {
      setErrors((prev) => ({
        ...prev,
        admin_contact_phone: !adminSameAsBusiness ? tv(validate('phone', value)) : '',
      }));
      return;
    }

    // Clear error only when the new value passes the same validation used on submit.
    if (!errors[field]) return;

    let fieldError = '';
    switch (field) {
      case 'company_address':
        fieldError = !value.trim()
          ? t('registrationStepsData.form.step2.companyAddress.errorRequired')
          : tv(validate('string', value));
        break;
      case 'company_website':
        fieldError = tv(validate('website', value));
        break;
      case 'business_contact_first_name':
        fieldError = !value.trim()
          ? t('registrationStepsData.form.step2.businessContact.firstName.errorRequired')
          : tv(validate('string', value));
        break;
      case 'business_contact_last_name':
        fieldError = !value.trim()
          ? t('registrationStepsData.form.step2.businessContact.lastName.errorRequired')
          : tv(validate('string', value));
        break;
      case 'business_email':
        fieldError = !value.trim()
          ? t('registrationStepsData.form.step2.businessContact.email.errorRequired')
          : tv(validate('email', value));
        break;
      case 'admin_contact_first_name':
        fieldError = !adminSameAsBusiness
          ? (!value.trim()
              ? t('registrationStepsData.form.step2.adminContact.firstName.errorRequired')
              : tv(validate('string', value)))
          : '';
        break;
      case 'admin_contact_last_name':
        fieldError = !adminSameAsBusiness
          ? (!value.trim()
              ? t('registrationStepsData.form.step2.adminContact.lastName.errorRequired')
              : tv(validate('string', value)))
          : '';
        break;
      case 'admin_contact_email':
        fieldError = !adminSameAsBusiness
          ? (!value.trim()
              ? t('registrationStepsData.form.step2.adminContact.email.errorRequired')
              : tv(validate('personalEmail', value)))
          : '';
        break;
      case 'api_schema_version':
        fieldError = !value || value.length === 0
          ? t('registrationStepsData.form.step1.question2.error')
          : '';
        break;
      case 'customer_type':
        fieldError = !value ? t('registrationStepsData.form.step2.businessModel.customers.errorRequired') : '';
        break;
      case 'business_model_type':
        fieldError = !value || value.length === 0
          ? t('registrationStepsData.form.step2.businessModel.model.errorRequired')
          : '';
        break;
      case 'support_flow':
        fieldError = !value ? t('registrationStepsData.form.step3.flow.error') : '';
        break;
      default:
        fieldError = '';
    }

    if (!fieldError) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  // ── "same as business" checkbox ─────────────────────────────────────────────
  const handleAdminSameToggle = (checked: boolean) => {
    setAdminSameAsBusiness(checked);
    if (checked) {
      setForm((prev: any) => ({
        ...prev,
        admin_contact_first_name: prev.business_contact_first_name,
        admin_contact_last_name: prev.business_contact_last_name,
        admin_contact_phone: prev.business_phone_no,
        admin_contact_email: prev.business_email,
      }));
      setIsDirty(true);
    }
  };

  // ── submit ──────────────────────────────────────────────────────────────────
  const handleSubmit = () => {
    const tv = (key: string) => (key ? t(key) : '');

    // ── field validation ────────────────────────────────────────────────────
    const newErrors: Record<string, string> = {
      company_address: !form.company_address.trim()
        ? t('registrationStepsData.form.step2.companyAddress.errorRequired')
        : tv(validate('string', form.company_address)),
      company_website: tv(validate('website', form.company_website)),
      business_contact_first_name: !form.business_contact_first_name.trim()
        ? t('registrationStepsData.form.step2.businessContact.firstName.errorRequired')
        : tv(validate('string', form.business_contact_first_name)),
      business_contact_last_name: !form.business_contact_last_name.trim()
        ? t('registrationStepsData.form.step2.businessContact.lastName.errorRequired')
        : tv(validate('string', form.business_contact_last_name)),
      business_phone_no: tv(validate('phone', form.business_phone_no)),
      business_email: !form.business_email.trim()
        ? t('registrationStepsData.form.step2.businessContact.email.errorRequired')
        : tv(validate('email', form.business_email)),
      admin_contact_first_name: !adminSameAsBusiness
        ? !form.admin_contact_first_name.trim()
          ? t('registrationStepsData.form.step2.adminContact.firstName.errorRequired')
          : tv(validate('string', form.admin_contact_first_name))
        : '',
      admin_contact_last_name: !adminSameAsBusiness
        ? !form.admin_contact_last_name.trim()
          ? t('registrationStepsData.form.step2.adminContact.lastName.errorRequired')
          : tv(validate('string', form.admin_contact_last_name))
        : '',
      admin_contact_phone: !adminSameAsBusiness
        ? tv(validate('phone', form.admin_contact_phone))
        : '',
      admin_contact_email: !adminSameAsBusiness
        ? !form.admin_contact_email.trim()
          ? t('registrationStepsData.form.step2.adminContact.email.errorRequired')
          : tv(validate('personalEmail', form.admin_contact_email))
        : '',
      api_schema_version: !form.api_schema_version || form.api_schema_version.length === 0
        ? t('registrationStepsData.form.step1.question2.error')
        : '',
      pos: (() => {
        const posItems = localPosItems !== null ? localPosItems : (compProfile?.pos_type || []).map((e: string) => { const p = e.trim().split(' '); return { code: p[p.length - 1], name: p.slice(0, -1).join(' ') } as AppliedPOSItem; });
        if (posItems.length === 0) return t('registrationStepsData.form.step1.question3.error');
        if (posItems.every(isPosOtherItem)) return t('registrationStepsData.form.step1.question3.onlyOtherError');
        return '';
      })(),
      customer_type: !form.customer_type
        ? t('registrationStepsData.form.step2.businessModel.customers.errorRequired')
        : '',
      business_model_type: !form.business_model_type || form.business_model_type.length === 0
        ? t('registrationStepsData.form.step2.businessModel.model.errorRequired')
        : '',
      support_flow: !form.support_flow
        ? t('registrationStepsData.form.step3.flow.error')
        : '',
    };

    setErrors(newErrors);

    // ── focus the first field with a validation error, in visual/tab order ────
    const focusOrder: Array<{ hasError: boolean; ref: React.RefObject<HTMLElement> }> = [
      { hasError: !!newErrors.company_address, ref: companyAddressRef },
      { hasError: !!newErrors.company_website, ref: companyWebsiteRef },
      { hasError: !!newErrors.customer_type, ref: customerTypeRef },
      { hasError: !!newErrors.business_model_type, ref: businessModelRef },
      { hasError: !!newErrors.pos, ref: editBtnRef },
      { hasError: !!newErrors.support_flow, ref: supportFlowRef },
      { hasError: !!newErrors.api_schema_version, ref: apiSchemaRef },
      { hasError: !!newErrors.business_contact_first_name, ref: businessFirstNameRef },
      { hasError: !!newErrors.business_contact_last_name, ref: businessLastNameRef },
      { hasError: !!newErrors.business_phone_no, ref: businessPhoneRef },
      { hasError: !!newErrors.business_email, ref: businessEmailRef },
      { hasError: !!newErrors.admin_contact_first_name, ref: adminFirstNameRef },
      { hasError: !!newErrors.admin_contact_last_name, ref: adminLastNameRef },
      { hasError: !!newErrors.admin_contact_phone, ref: adminPhoneRef },
      { hasError: !!newErrors.admin_contact_email, ref: adminEmailRef }
    ];

    if (Object.values(newErrors).some(Boolean)) {
      const target = focusOrder.find((e) => e.hasError)?.ref;
      setTimeout(() => {
        target?.current?.focus();
        target?.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }, 100);
      return;
    }

    try {
      // Map customer type display name → id
      // Try defaultCompData first, then fall back to the computed customerTypeOptions
      const ctMatch =
        (defaultCompData?.customer_types || []).find(
          (ct: any) =>
            ct.customer_type === form.customer_type ||
            ct.customer_type_fr === form.customer_type ||
            ct.customer_type_id === form.customer_type
        ) || customerTypeOptions.find((opt: any) => opt.label === form.customer_type || opt.id === form.customer_type);
      const customer_type_id = (ctMatch as any)?.customer_type_id || (ctMatch as any)?.id || form.customer_type;

      // Map business model display names → ids
      // Try defaultCompData first, then fall back to the computed bizModelOptions
      const business_model_id: string[] = [];
      (form.business_model_type || []).forEach((label: string) => {
        const found =
          (defaultCompData?.business_model || []).find(
            (bm: any) =>
              bm.business_model_type === label ||
              bm.business_model_type_fr === label ||
              bm.business_model_id === label
          ) || bizModelOptions.find((opt: any) => opt.label === label || opt.id === label);
        const id = (found as any)?.business_model_id || (found as any)?.id;
        if (id) business_model_id.push(id);
      });

      // Map POS types → ISO-2 codes (use locally edited POS if available)
      const currentPosCodes = localPosItems !== null
        ? localPosItems.map((p) => p.code)
        : (compProfile?.pos_type || []).map((posEntry: string) => {
            const parts = posEntry.trim().split(' ');
            return parts[parts.length - 1];
          });

      // Format date to ISO — build from local date parts so the calendar date is
      // preserved regardless of the user's timezone (avoids .toISOString() shifting
      // a local midnight date into the previous UTC day for timezones behind UTC).
      let implementation_start_date = '';
      if (form.implementation_start_date) {
        const _d = form.implementation_start_date as Date;
        if (!isNaN(_d.getTime())) {
          const yr = _d.getFullYear();
          const mo = String(_d.getMonth() + 1).padStart(2, '0');
          const dy = String(_d.getDate()).padStart(2, '0');
          implementation_start_date = `${yr}-${mo}-${dy}T00:00:00.000Z`;
        }
      }

      // Build business_model_type as [{id, iata}]
      const business_model_type_payload = business_model_id.map((id: string) => {
        const bm = (defaultCompData?.business_model || []).find((b: any) => b.business_model_id === id);
        const iataRequired = bm?.iata_mandatory === 1;
        return {
          id,
          iata: iataRequired ? (parseInt(form.iata_number, 10) || null) : null,
        };
      });

      // Build pos_preference as [{id, iata}]
      const pos_preference_payload = currentPosCodes.map((code: string) => ({
        id: code,
        iata: localIataValues[code] ? (parseInt(localIataValues[code], 10) || null) : null,
      }));

      // Map api_schema_version display labels → codes (e.g. "Other" → "AS001")
      const apiSchemaDescToCode: Record<string, string> = {};
      (regApiSchemas?.[lang] || []).forEach((s: any) => {
        if (s.desc && s.code) apiSchemaDescToCode[s.desc] = s.code;
      });
      const api_schema_version_payload = form.api_schema_version.map(
        (v: string) => apiSchemaDescToCode[v] || v
      );

      const directPayload = {
        company_address: form.company_address,
        company_website: form.company_website,
        business_contact_first_name: form.business_contact_first_name,
        business_contact_last_name: form.business_contact_last_name,
        business_phone_no: form.business_phone_no,
        business_email: form.business_email,
        admin_contact_first_name: form.admin_contact_first_name,
        admin_contact_last_name: form.admin_contact_last_name,
        admin_contact_email: form.admin_contact_email,
        admin_contact_phone: form.admin_contact_phone,
        customer_type_id,
        business_model_type: business_model_type_payload,
        api_schema_version: api_schema_version_payload,
        pos_preference: pos_preference_payload,
        implementation_start_date,
        support_flow: [form.support_flow].filter(Boolean),
        existing_integration: Boolean(compProfile?.existing_integration),
        existing_integration_comment: compProfile?.existing_integration_comment ?? '',
        supported_by_IT: true,
      };

      dispatch(updateCompanyProfileStart({ payload: directPayload, language: i18n.language }));
    } catch (err: any) {
      console.error('Error building profile update payload', err);
      dispatch(
        setShowSuccessPopUp({
          message: t('sellerProfile.dashboard.errorGeneral'),
          successType: 'error',
        })
      );
    }
  };

  if (!ready) return <></>;

  const supportFlowDisplayValue = (() => {
    const desc = supportedFlowCodeToDesc[form.support_flow] || form.support_flow;
    return supportedFlowOptions.map((sf) => sf.desc).includes(desc) ? desc : '';
  })();

  return (
    <Box className="cpEditProfile">
      {/* ── Header row ────────────────────────────────────────────────────── */}
      <Box className="cpHeadingRow">
        <Typography component="h1" className="cpCompanyHeading">
          {company_name}
        </Typography>
      </Box>

      <Box
        className="cpFormCard"
        ref={cardRef}
        sx={{ minHeight: pickerHeight > 0 ? `${pickerHeight}px` : 'auto'}}
      >
        {/* ── Commercial details ───────────────────────────────────────── */}
        <Box className="cpSection">
          <Box className="cpCommercialHeader">
            <Typography component="h2" className="cpSectionTitle">
              {lbl('commercialDetails') || 'Commercial details'}
            </Typography>
            <Typography className="indicator"><span style={{ color: '#FF333A', marginLeft: 2 }}>*</span>&nbsp;{lbl('indicator')}</Typography>
          </Box>
          {/* ── Business model information ───────────────────────────────── */}
          <Box className="cpTwoColGrid">
            <Box>
              <CPTextField
                label={lbl('coAddress') || 'Company address'}
                value={form.company_address}
                onChange={(v) => handleChange('company_address', v)}
                error={!!errors.company_address}
                errorMessage={errors.company_address}
                required
                inputRef={companyAddressRef}
                startIcon={<SvgIcon src="/images/registration/AddressIcon.svg" size={24} sx={fieldIconSx} color={errors.company_address ? '#9E231B' : '#1460AA'} />}
              />
            </Box>
            <Box>
              <CPTextField
                label={lbl('coWebsite') || 'Company website'}
                value={form.company_website}
                onChange={(v) => handleChange('company_website', v)}
                error={!!errors.company_website}
                errorMessage={errors.company_website}
                inputRef={companyWebsiteRef}
                startIcon={<SvgIcon src="/images/registration/websiteIcon.svg" size={24} sx={fieldIconSx} color={errors.company_website ? '#9E231B' : '#1460AA'} />}
              />
            </Box>

            {/* Company type */}
            <Box className="dropdownMarginTop selectCss">
              <CommonSelect
                label={lbl('coType') || 'Company type'}
                value={customerTypeOptions.map((opt: any) => opt.label).includes(form.customer_type) ? form.customer_type : (customerTypeIsLegacy ? form.customer_type : '')}
                onChange={(v) => handleChange('customer_type', v)}
                menuList={customerTypeOptions.map((opt: any) => opt.label)}
                error={!!errors.customer_type || customerTypeIsLegacy}
                errorMessage={customerTypeIsLegacy ? t('registrationStepsData.form.step2.businessModel.customers.errorLegacyUpdate') : errors.customer_type}
                required
                triggerRef={customerTypeRef}
              />
            </Box>

            {/* Business model */}
            <Box className="dropdownMarginTop selectCss">
              <CPMultiSelect
                label={lbl('bizModel') || 'Business model'}
                values={form.business_model_type.filter((v: string) => bizModelOptions.map((opt: any) => opt.label).includes(v))}
                onChange={(v) => handleChange('business_model_type', v)}
                menuList={bizModelOptions.map((opt: any) => opt.label)}
                error={!!errors.business_model_type}
                errorMessage={errors.business_model_type}
                required
                triggerRef={businessModelRef}
              />
            </Box>
          </Box>

          {/* POS implementation (read-only + Edit POS link) */}
          <Box className="cpPosRow">
            <Box className="cpPosHeader">
              <Typography className="cpFieldLabel">
                {lbl('acPOS') || 'POS implementation'}
                <span style={{ color: '#FF333A', marginLeft: 2 }}>*</span>
              </Typography>
              <Button
                variant="text"
                size="small"
                ref={editBtnRef}
                className="cpEditPosLink"
                onClick={() => setPosModalOpen(true)}
                sx={{ textDecoration: 'underline', textUnderlineOffset: '1px','cursor': 'pointer', '&:focus-within':{outline: '2px solid black', outlineOffset: '2px' }}} 

              >
                <SvgIcon src="/images/registration/editIcon.svg" size={16} color="#1460AA" />
                {lbl('editPOS') || 'Edit POS'}
              </Button>
            </Box>
            {errors.pos && <FieldError message={errors.pos} />}
            <ul className="cpPosFlags" aria-label={lbl('acPOS') || 'POS implementation'}>
              {displayPosItems.map((p: any, i: number) => (
                <li key={i} className={`cpPosItem${i >= 2 ? ' cpPosItemHideOnMobile' : ''}`}>
                  {(p.iso3 || p.flag) && (
                    <img
                      src={p.flag || `/images/flags/${p.iso3}.png`}
                      alt={p.name}
                      className="cpPosFlag"
                    />
                  )}
                  <Box>
                    <Typography className="cpPosName">{p.name}</Typography>
                    {p.iata && <Typography className="cpPosIata">{p.iata}</Typography>}
                  </Box>
                </li>
              ))}
              {displayPosItems.length > 2 && (
                <li className="cpPosOverflow" aria-label={`+${displayPosItems.length - 2} more`}>
                  +{displayPosItems.length - 2}
                </li>
              )}
            </ul>
          </Box>
        </Box>

        {/* ── Technical details ────────────────────────────────────────── */}
        <Box className="cpSection">
          <Typography component="h2" className="cpSectionTitle">
            {lbl('technicalDetails') || 'Technical details'}
          </Typography>
          <Box className="cpThreeColGrid">
            {/* Implementation start date */}
            <Box className="cpDatePicker">
              <CommonDatePicker
                label={lbl('impDate') || 'Implementation start date'}
                value={form.implementation_start_date}
                onChange={(date) => handleChange('implementation_start_date', date)}
                onOpenChange={handlePickerOpenChange}
              />
            </Box>

            {/* Supported flow */}
            <Box className="dropdownMarginTop">
              <Box className={`cpSupportedFlow${supportFlowDisplayValue ? ' cpSupportedFlowHasValue': ''} selectCss`}>
                <CommonSelect
                  label={lbl('supportedFlow') || 'Supported flow'}
                  value={supportFlowDisplayValue}
                  onChange={(v) => handleChange('support_flow', supportedFlowDescToCode[v] || v)}
                  menuList={supportedFlowOptions.map((sf) => sf.desc)}
                  error={!!errors.support_flow}
                  errorMessage={errors.support_flow}
                  required
                  triggerRef={supportFlowRef}
                />
              </Box>
            </Box>
            {/* API schema */}
            <Box className="cpApiSchemaField dropdownMarginTop selectCss">
              <CommonMultiSelect
                label={lbl('apiSchema') || 'API schema'}
                values={(() => {
                  if (!apiSchemaOptions.length) return form.api_schema_version as string[];
                  const otherOption = apiSchemaOptions.find((opt: string) => apiSchemaCodeToDesc['AS001'] === opt);
                  const mapped = (form.api_schema_version as string[]).map((v: string) => {
                    if (apiSchemaOptions.includes(v)) return v;
                    const desc = apiSchemaCodeToDesc[v];
                    if (desc && apiSchemaOptions.includes(desc)) return desc;
                    return otherOption ?? null;
                  }).filter(Boolean) as string[];
                  const deduped = Array.from(new Set(mapped));
                  // Sort selected values with "Other" (AS001) at the end
                  return deduped.sort((a, b) => {
                    const aIsOther = apiSchemaCodeToDesc['AS001'] === a;
                    const bIsOther = apiSchemaCodeToDesc['AS001'] === b;
                    if (aIsOther && !bIsOther) return 1;
                    if (!aIsOther && bIsOther) return -1;
                    return 0;
                  });
                })()}
                onChange={(v) => handleChange('api_schema_version', v.filter((val: string) => val !== '-'))}
                menuList={apiSchemaOptions}
                error={!!errors.api_schema_version}
                errorMessage={errors.api_schema_version}
                required
                triggerRef={apiSchemaRef}
              />
            </Box>
          </Box>
        </Box>


        {/* ── Contacts ─────────────────────────────────────────────────── */}
        <Box className="cpSection cpContactsSection">
          {/* Business contact */}
          <Box className="cpContactBlock">
            <Box className="cpContactBlockHeader">
              <Typography component="h2" className="cpSectionTitle">
                {lbl('businessContact') || 'Business contact'}
              </Typography>
            </Box>
            <Box className="cpTwoColGrid">
              <Box>
                <CPTextField
                  label={lbl('firstName') || 'First name'}
                  value={form.business_contact_first_name}
                  onChange={(v) => handleChange('business_contact_first_name', v)}
                  error={!!errors.business_contact_first_name}
                  errorMessage={errors.business_contact_first_name}
                  required
                  inputRef={businessFirstNameRef}
                />
              </Box>
              <Box>
                <CPTextField
                  label={lbl('lastName') || 'Last name'}
                  value={form.business_contact_last_name}
                  onChange={(v) => handleChange('business_contact_last_name', v)}
                  error={!!errors.business_contact_last_name}
                  errorMessage={errors.business_contact_last_name}
                  required
                  inputRef={businessLastNameRef}
                />
              </Box>
              <Box>
                <CPTextField
                  label={lbl('phone') || 'Phone number'}
                  value={form.business_phone_no}
                  onChange={(v) => handleChange('business_phone_no', v)}
                  error={!!errors.business_phone_no}
                  errorMessage={errors.business_phone_no}
                  inputRef={businessPhoneRef}
                  startIcon={<SvgIcon src="/images/registration/phoneIcon.svg" size={20} sx={fieldIconSx} color={errors.business_phone_no ? '#9E231B' : '#1460AA'} />}
                />
              </Box>
              <Box>
                <CPTextField
                  label={lbl('email') || 'Email'}
                  value={form.business_email}
                  onChange={(v) => handleChange('business_email', v)}
                  error={!!errors.business_email}
                  errorMessage={errors.business_email}
                  required
                  inputRef={businessEmailRef}
                  startIcon={<SvgIcon src="/images/registration/emailIcon.svg" size={20} sx={fieldIconSx} color={errors.business_email ? '#9E231B' : '#1460AA'} />}
                />
              </Box>
            </Box>
          </Box>


          {/* Admin contact */}
          <Box className="cpContactBlock">
            <Box className="cpContactBlockHeader cpAdminContactHeader">
              <Typography component="h2" className="cpSectionTitle">
                {lbl('adminContactDetails') || 'Admin contact'}
              </Typography>
              <FormControlLabel
                className="cpSameAsCheckbox"
                control={
                  <Checkbox
                    checked={adminSameAsBusiness}
                    onChange={(e) => handleAdminSameToggle(e.target.checked)}
                    sx={{
                      width: '18px', height: '18px', alignSelf: 'center',
                      color: '#1460AA', '&.Mui-checked': { color: '#1460AA' }}}
                  />
                }
                label={
                  <Typography className="cpSameAsLabel">
                    {lbl('adminSameAsBusiness') || 'Make Admin contact details same as Business contact details'}
                  </Typography>
                }
              />
            </Box>
            <Box className="cpTwoColGrid">
              <Box>
                <CPTextField
                  label={lbl('firstName') || 'First name'}
                  value={form.admin_contact_first_name}
                  onChange={(v) => handleChange('admin_contact_first_name', v)}
                  disabled={adminSameAsBusiness}
                  error={!!errors.admin_contact_first_name}
                  errorMessage={errors.admin_contact_first_name}
                  required={!adminSameAsBusiness}
                  inputRef={adminFirstNameRef}
                />
              </Box>
              <Box>
                <CPTextField
                  label={lbl('lastName') || 'Last name'}
                  value={form.admin_contact_last_name}
                  onChange={(v) => handleChange('admin_contact_last_name', v)}
                  disabled={adminSameAsBusiness}
                  error={!!errors.admin_contact_last_name}
                  errorMessage={errors.admin_contact_last_name}
                  required={!adminSameAsBusiness}
                  inputRef={adminLastNameRef}
                />
              </Box>
              <Box>
                <CPTextField
                  label={lbl('phone') || 'Phone number'}
                  value={form.admin_contact_phone}
                  onChange={(v) => handleChange('admin_contact_phone', v)}
                  disabled={adminSameAsBusiness}
                  error={!!errors.admin_contact_phone}
                  errorMessage={errors.admin_contact_phone}
                  inputRef={adminPhoneRef}
                  startIcon={<SvgIcon src="/images/registration/phoneIcon.svg" size={24} sx={fieldIconSx} color={errors.admin_contact_phone ? '#9E231B' : '#1460AA'} />}
                />
              </Box>
              <Box>
                <CPTextField
                  label={lbl('email') || 'Email'}
                  value={form.admin_contact_email}
                  onChange={(v) => handleChange('admin_contact_email', v)}
                  disabled={adminSameAsBusiness}
                  error={!!errors.admin_contact_email}
                  errorMessage={errors.admin_contact_email}
                  required={!adminSameAsBusiness}
                  inputRef={adminEmailRef}
                  startIcon={<SvgIcon src="/images/registration/emailIcon.svg" size={20} sx={fieldIconSx} color={errors.admin_contact_email ? '#9E231B' : '#1460AA'} />}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* ── bottom actions ─────────────────────────────────────── */}
        <Box className="cpMobileActions cpSection">
        <Button
          variant="outlined"
          className="cpCancelBtn "
          onClick={() => navigate(`/${i18n.language}/seller-profile`)}
        >
          {t('sellerProfile.dashboard.cancel')}
        </Button>
         <Button
          variant="contained"
          className="cpUpdateBtn"
          onClick={handleSubmit}
          disabled={updating || !isDirty || customerTypeIsLegacy}
          startIcon={updating ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {lbl('updateProfile') || t('sellerProfile.dashboard.saveUpdates')}
        </Button>
      </Box>
      </Box>      
      <SuperModalRegistration
        open={posModalOpen}
        collectIata={anyIataRequired}
        onClose={() => {setPosModalOpen(false); requestAnimationFrame(() => {
            editBtnRef.current?.focus();
          });}}
        onNext={(items) => {
          handlePosNext(items); requestAnimationFrame(() => {
            editBtnRef.current?.focus();
          });
        }}
        onOtherSelected={handlePosOtherSelected}
        initialSelected={(localPosItems ?? posItems).map((p: any) => p.code)}
        initialIataValues={localIataValues}
      />
    </Box>
  );
}

export default CompanyProfile;
